import { RouterClient, RouterListenerUnsubscribe } from "../../clients/routerClient";
import { Logger } from "../../clients/logger";
import PublicEventManager, { Unsubscriber } from "./PublicEventManager";
import FinsembleEvent from "./PrivateFinsembleEvent";
import { sleep } from "../util";
import { LAUNCHER_SERVICE } from "../../common/constants";

type EventOptions = {
	initialQueryTimeout: Number;
	timeout: Number;
};

/**
 * This private Event Manager distributes events to remove event handlers (using the router).
 * It is used within the windowService and workspaceService. Clients use the PublicEventManager
 * in order to register and unregister handlers.
 *
 * The primary use is for FinsembleWindow events. FinsembleWindow is derived from PublicEventManager.
 *
 * The windowService will create multiple PrivateEventManagers, generally one per live window. Each
 * PrivateEventManager maintains its own private store of remote event handlers. Note that event handlers
 * may exist across multiple windows. For instance, a window called "Bobby" may want to know when another
 * window called "Gary" closes. When Bobby registers an event handler ("tell me when Gary closes") then
 * Gary's PrivateEventManager will gain a handler, which is associated with Bobby.
 *
 * In the above scenario, if Bobby (client) closes then Gary's PrivateEventManager will remove all references
 * to remote event handlers. But, if Gary closes then Bobby's references will become orphans - never triggered,
 * which is probably fine for most application logic.
 *
 * For more information on the distrubted event architecture - see https://miro.com/app/board/o9J_kwhN9zo=/
 *
 * Note, this class can also be used as a *local* event manager with the functions.
 */
class EventManager extends PublicEventManager {
	/**
	 * Listens for [graceful] window closings and then removes event listeners associated with the closed window
	 */
	#unsubscribeWindowClosings: RouterListenerUnsubscribe | undefined;

	eventList: string[] = [];

	eventInternalHandlerMap: Record<string, string[]> = {};

	remoteListenerEventMap: Record<string, string[]> = {};

	windowToGuidsMap: Record<string, string[]> = {};

	waitList: Record<string, Function> = {};

	unsubscribes: RouterListenerUnsubscribe[] = [];

	/**
	 *Creates an instance of EventManager.
	 * @param {*} params
	 * @memberof EventManager
	 */
	constructor(params: { name: string; sourceType?: string }) {
		super(params);

		// listen for remote events registering
		this.unsubscribes.push(
			RouterClient.addListener(`Finsemble.RegisterInterruptibleEvent.${this.name}`, (err, response) => {
				this.registerRemoteEventHandler(response.data.event, response.data.listenerGuid, response.data.windowName);
			})
		);

		//listen for remote events unregistering
		this.unsubscribes.push(
			RouterClient.addListener(`Finsemble.UnregisterInterruptibleEvent.${this.name}`, (err, response) => {
				this.unregisterRemoteEventHandler(response.data.event, response.data.listenerGuid, response.data.windowName);
			})
		);

		/**
		 * Listen on Finsemble's global window closed pubsub. When a window is closed all local listeners are removed.
		 */
		this.#unsubscribeWindowClosings = RouterClient.addListener(LAUNCHER_SERVICE.WINDOW_CLOSED, (err, response) => {
			if (err) {
				Logger.system.error(`PrivateEventManager: Error listening for ${LAUNCHER_SERVICE.WINDOW_CLOSED}: ${err}`);
			} else {
				const { name } = response.data;
				this.removeWindow(name);
			}
		});
	}

	/**
	 * Removes all listeners associated with a window [that has closed]
	 */
	private removeWindow(listeningWindow: string) {
		(this.windowToGuidsMap[listeningWindow] ?? []).forEach((listenerGuid) => {
			this.removeGuid(listenerGuid);
		});
		delete this.windowToGuidsMap[listeningWindow];
	}

	/**
	 * Removes all listeners for a specific remote guid
	 */
	private removeGuid(listenerGuid: string) {
		const events = Object.keys(this.remoteListenerEventMap);
		events.forEach((event) => {
			this.remoteListenerEventMap[event] = this.remoteListenerEventMap[event].filter(
				(oldGuid) => oldGuid !== listenerGuid
			);
		});
	}

	/**
	 * Adds an individual remote listener (listenerGuid+event) to this event manager
	 */
	registerRemoteEventHandler(event: string, listenerGuid: string, listenerWindow: string): void {
		if (!this.remoteListenerEventMap[event]) this.remoteListenerEventMap[event] = [];
		this.remoteListenerEventMap[event].push(listenerGuid);
		this.windowToGuidsMap[listenerWindow] = [listenerGuid].concat(this.windowToGuidsMap[listenerWindow] ?? []);
	}

	/**
	 * Removes an individual remote listener (listenerGuid+event) from this event manager
	 */
	unregisterRemoteEventHandler(event: string, listenerGuid: string, listenerWindow: string): void {
		if (this.remoteListenerEventMap[event]) {
			// Remove the remote listener
			this.remoteListenerEventMap[event] = this.remoteListenerEventMap[event].filter((g) => g !== listenerGuid);
			// Remove from the window map. Delete the window map if there are no more listeners.
			this.windowToGuidsMap[listenerWindow] = (this.windowToGuidsMap[listenerWindow] ?? []).filter(
				(g) => g !== listenerGuid
			);
			if (this.windowToGuidsMap[listenerWindow].length === 0) delete this.windowToGuidsMap[listenerWindow];
		} else if (this.windowToGuidsMap[listenerWindow]) {
			// if window is still open
			// Straggling unregister requests may trickle in after a window is closed, but if the window is still open then something has probably gone wrong.
			Logger.system.warn(
				`PrivateEventManager.unregisterListener() attempt to remove unregistered event "${event}" for window ${listenerWindow}`
			);
		}
	}

	/**
	 * We must wait for a remote transit if delayed
	 *
	 * @memberof EventManager
	 */
	awaitRemoteDelayer(eventGuid: string, listenerGuid: string): Promise<Function> {
		const awaitRemoteDelayerPromiseResolver = (resolve, reject) => {
			const listener = (err, response) => {
				RouterClient.removeListener(`Finsemble.Listener.Done.${eventGuid}.${listenerGuid}`, listener);
				if (response.data.canceled === true) {
					reject("canceled");
				} else {
					resolve(response.data.data);
				}
			};
			this.waitList[eventGuid] = listener;
			RouterClient.addListener(`Finsemble.Listener.Done.${eventGuid}.${listenerGuid}`, listener);
		};
		return new Promise(awaitRemoteDelayerPromiseResolver);
	}

	/**
	 * Sends a message to a remote listener (PublicEventManager) which will then emit a local Finsemble event.
	 *
	 * Called by this.trigger().
	 */
	private async queryRemoteListener(
		event: string,
		listenerGuid: string,
		data: any,
		options: EventOptions
	): Promise<any> {
		// This needs to get added first, otherwise the event can be done before the listener is created; only do this for delayable events.
		const eventGuid = `${this.name}_private_${Date.now()}_${Math.random()}`;
		const eventData = {
			event,
			guid: eventGuid,
			data,
		};

		const delayListener = this.awaitRemoteDelayer(eventGuid, listenerGuid);

		// 1 second to respond to initial query - if this doesn't happen, the window that has the listener is likely crashed or uncleanly closed
		// We send the query without a timeout because there's a race condition. This interrupt may be delivered just as the
		// app is shutting down, in which case there will be no responder available to process this event.
		const { err, response }: any = await Promise.race([
			RouterClient.query(
				`Finsemble.Interrupt.${listenerGuid}`,
				eventData,
				{ timeout: 0, logWhenNoResponder: false },
				() => {}
			),
			sleep(options.initialQueryTimeout, true),
		]).catch(() => {
			// sleep throws an exception
			RouterClient.removeListener(`Finsemble.Listener.Done.${eventGuid}.${listenerGuid}`, this.waitList[eventGuid]);
			throw "eventHandler failed initial response";
		});

		if (err || !response.data) return;

		let needToRemoveListener = true;
		if (response.data.wait === true) {
			try {
				needToRemoveListener = false;
				return await delayListener;
			} catch (e) {
				if (e === "canceled") {
					// the only cancelable event is 'close-request'. The window/workspace close is canceled  don't remove the listener
					throw "event was canceled";
				} else {
					needToRemoveListener = true;
				}
			}
		}
		if (response.data.delayed === true) {
			try {
				needToRemoveListener = false;
				// must respond within timeout seconds
				return await Promise.race([delayListener, sleep(options.timeout, true)]);
			} catch (e) {
				// sleep throws an exception
				if (e !== "timeout expired") {
					throw "event was canceled";
				} else {
					needToRemoveListener = true;
				}
			}
		}
		if (needToRemoveListener) {
			RouterClient.removeListener(`Finsemble.Listener.Done.${eventGuid}.${listenerGuid}`, this.waitList[eventGuid]);
		}

		if (response.data.canceled === true) {
			throw "event was canceled";
		}

		return response.data.data;
	}

	/**
	 * Forwards an event to all remote listeners and then waits for them to respond.
	 * They may optionally interrupt the event by sending a "cancel" which results in FinsembleEvent throwing
	 * an exception. Any individual exception will cause this function to also throw an exception, thus
	 * interrupting the event (which occurs upstream, e.g. preventing a window from closing).
	 */
	async trigger(
		event: string,
		data: any = null,
		options: EventOptions = { initialQueryTimeout: 1000, timeout: 10000 }
	): Promise<any[] | void> {
		Logger.verbose(`Triggering ${event} for ${this.name}`, data);
		const listenerList: Promise<any>[] = [];

		// If a tree falls in the forest and nobody is listening, does it matter?
		if (this.remoteListenerEventMap[event]) {
			// send a query all remote listeners
			for (let listenerGuid of this.remoteListenerEventMap[event]) {
				listenerList.push(this.queryRemoteListener(event, listenerGuid, data, options));
			}
		}

		// Send the event to each registered handler
		const handlers = this.handlers.filter((entry) => entry.event === event);

		handlers.forEach((entry) => {
			const finsembleEvent = new FinsembleEvent({
				source: this.name,
				sourceType: this.sourceType,
				handler: entry.handler,
				event,
				data,
				guid: entry.listenerGuid,
			});
			listenerList.push(finsembleEvent.complete);
		});

		try {
			return await Promise.all(listenerList);
		} catch (err) {
			if (err === "eventHandler failed initial response") {
				console.debug(`No response for interrupt on event ${event}. Window is probably already closed.`);
			} else {
				throw "event was canceled";
			}
		}
	}

	/**
	 * Adds a *local* event listener.
	 */
	addEventListener(event: string, handler: Function): Unsubscriber {
		Logger.system.debug("PrivateEventManager.addEventListener:", event);

		const listenerGuid = `${this.name}_public_${Date.now()}_${Math.random()}`;

		this.handlers.push({
			event,
			handler,
			listenerGuid,
		});

		return () => {
			this.removeEventListener(event, handler);
		};
	}

	/**
	 * Removes a *local* event listener.
	 */
	removeEventListener(event: string, handler: Function): void {
		Logger.system.debug("PrivateEventManager.removeEventListener:", event);
		// Find the first entry that matches - noting that there could be multiple entries for the same handler
		// and this legacy interface provides no disambiguation via listenerGuid
		const index = this.handlers.findIndex((entry) => entry.event === event && entry.handler === handler);

		if (index == -1) {
			Logger.system.warn(`PrivateEventManager.removeEventListener() attempt to remove unregistered event "${event}"`);
			return;
		}

		this.handlers.splice(index, 1);
	}

	/**
	 * Removes all router listeners and subscriptions. This is called when the windowService terminates this
	 * PrivateEventManager (e.g. because the window has closed).
	 */
	cleanup() {
		if (this.#unsubscribeWindowClosings) this.#unsubscribeWindowClosings();

		// Close the router listeners that were opened in the constructor
		this.unsubscribes.forEach((unsubscribe) => {
			unsubscribe();
		});
		this.unsubscribes = [];
	}
}

export default EventManager;
